import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/about", label: "About" },
  { to: "/platform", label: "Platform" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-sand bg-cream/80 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-terracotta font-display text-sm font-bold text-cream transition-colors duration-300 group-hover:bg-terracotta-dark">
            WH
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-ink">
            Winnie Ho
          </span>
        </Link>

        <nav data-testid="nav-desktop" className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:bg-terracotta after:transition-all after:duration-300 ${
                  isActive ? "text-terracotta after:w-full" : "text-stone2 hover:text-ink after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/get-involved"
            data-testid="nav-volunteer-cta"
            className="rounded-sm bg-terracotta px-5 py-2.5 text-sm font-semibold text-cream transition-all duration-300 hover:bg-terracotta-dark active:scale-[0.98]"
          >
            Volunteer
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center text-ink lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-sand bg-cream lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(" ", "-")}`}
                  className="py-3 font-display text-2xl font-bold tracking-tight text-ink transition-colors hover:text-terracotta"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/get-involved"
                data-testid="nav-mobile-volunteer-cta"
                className="mt-4 rounded-sm bg-terracotta px-5 py-3 text-center font-semibold text-cream"
              >
                Volunteer
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
